import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { CircularProgress, Collapse, Typography } from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import { useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import {
  MonitoringUpdateRatePlanrResponse,
  MonitoringUpdateRoomTypeFieldsType,
  MonitoringUpdateRoomTypeLayer
} from '../../types/monitoringUpdate'
import RatePlaneLayer from './RatePlaneLayer'
import RoomTypeLayerRowMenu from './RoomTypeLayerRowMenu'
import RowBoxValuesRoomTypeMenu from './RowBoxValuesRoomTypeMene'
import useStyles from './Styles/RoomTypeLayer.Style'

interface RoomTypeLayerProps {
  dataRoomType: MonitoringUpdateRoomTypeLayer
  range: 'TWO_WEEKS' | 'MONTH'
  weekendIndexes: number[]
  indexRoomType: number
  showChannelsLayer?: boolean
  ratePlanMonitoringUpdate: MonitoringUpdateRatePlanrResponse | undefined
  getMonitoringRatePlanData: LazyQueryExecFunction<
    {
      getUpdateMonitoringRatePlanLayer: MonitoringUpdateRatePlanrResponse
    },
    OperationVariables
  >
  inventoryTypeAutocomplete: any
  statusAutocomplete: any
  selectedDateInDatePicker: DateObject
  propertyCode: string
  channelID?: string
  loadingForRatePlan: boolean
  currentIndex: number
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
}

export default function RoomTypeLayer({
  range,
  dataRoomType,
  weekendIndexes,
  showChannelsLayer,
  ratePlanMonitoringUpdate,
  getMonitoringRatePlanData,
  inventoryTypeAutocomplete,
  statusAutocomplete,
  selectedDateInDatePicker,
  propertyCode,
  channelID,
  loadingForRatePlan,
  indexRoomType,
  currentIndex,
  setCurrentIndex
}: RoomTypeLayerProps) {
  const classes = useStyles()
  const [showRatePlans, setShowRatePlans] = useState(false)
  const PAGE_SIZE = 5
  const [numberOfVisibleRatePlans, setnumberOfVisibleRatePlans] =
    useState(PAGE_SIZE)
  const [selectedRoomTypeRowMenu, setSelectedRoomTypeRowMenu] =
    useState<MonitoringUpdateRoomTypeFieldsType>('default')
  const mayItemMenu: {
    value: MonitoringUpdateRoomTypeFieldsType
    label: string
  }[] = [
    { value: 'default', label: 'default' },
    { value: 'availability', label: 'availability' },
    { value: 'pooledAvailability', label: 'pooledAvailability' },
    {
      value: 'totalAllocatedAvailabiliy',
      label: 'totalAllocatedAvailability'
    }
  ]
  const handleChangeSelectedInventoryFieldInRoomType = (
    fieldType: MonitoringUpdateRoomTypeFieldsType
  ) => {
    setSelectedRoomTypeRowMenu(fieldType)
  }

  const clickOnRatePlan = (index: number) => {
    if (index === indexRoomType) {
      setShowRatePlans(!showRatePlans)
    }
    setCurrentIndex(index)
    getMonitoringRatePlanData({
      variables: {
        filters: {
          inventoryType:
            inventoryTypeAutocomplete && inventoryTypeAutocomplete.value,
          from: selectedDateInDatePicker.gregorianStartDate,
          to: selectedDateInDatePicker.gregorianEndDate,
          updateHealthStatus: statusAutocomplete
            ? statusAutocomplete.value
            : undefined,
          propertyCode: propertyCode,
          roomTypeCode: dataRoomType.roomTypeCode,
          channelID: channelID ? channelID : undefined
        }
      }
    })
  }

  return (
    <div>
      <div
        className={[
          showChannelsLayer ? classes.roomTypeRowGrey : classes.roomTypeRow,
          range === 'MONTH' ? classes.roomTypeRowForMonth : ''
        ].join(' ')}>
        <div className={classes.rowNameContainer}>
          <div className={classes.roomIcon}>
            <div
              className={[
                showChannelsLayer ? classes.rowNameMarginLeft : classes.rowName
              ].join(' ')}>
              <Typography variant="h5" className={classes.rowNameTitle}>
                {dataRoomType.roomTypeName}
              </Typography>
              <Typography
                variant="h6"
                className={classes.ratePlan}
                component="span"
                onClick={() => clickOnRatePlan(indexRoomType)}>
                {`${dataRoomType.numberOfRatePlans} ${getTranslate(
                  'Rate Plans'
                )}`}
              </Typography>
              {loadingForRatePlan && indexRoomType === currentIndex ? (
                <CircularProgress size={12} />
              ) : (
                <ArrowRightIcon
                  onClick={() => clickOnRatePlan(indexRoomType)}
                  className={[
                    classes.ratePlanArrowRight,
                    showRatePlans ? classes.ratePlanArrowRightCollapsed : ''
                  ].join(' ')}
                />
              )}
            </div>
          </div>
        </div>
        <div className={classes.dayContainer}>
          <RoomTypeLayerRowMenu
            dataRoomType={dataRoomType}
            mayItemMenu={mayItemMenu}
            selectedRoomTypeRowMenu={selectedRoomTypeRowMenu}
            setSelectedRoomTypeRowMenu={setSelectedRoomTypeRowMenu}
            onChangeSelectedItem={(newMenuItem: string) =>
              handleChangeSelectedInventoryFieldInRoomType(
                newMenuItem as MonitoringUpdateRoomTypeFieldsType
              )
            }
          />
          <ArrowRightIcon
            className={[
              classes.channelArrowRight,
              classes.channelArrowRightCollapsed
            ].join(' ')}
          />
        </div>
        <RowBoxValuesRoomTypeMenu
          weekendIndexes={weekendIndexes}
          range={range}
          dataRoomType={dataRoomType}
          selectedRoomTypeRowMenu={selectedRoomTypeRowMenu}
        />
      </div>
      <Collapse in={showRatePlans}>
        <div>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => {
              setTimeout(() => {
                setnumberOfVisibleRatePlans(
                  numberOfVisibleRatePlans + PAGE_SIZE
                )
              }, 500)
            }}
            hasMore={
              ratePlanMonitoringUpdate &&
              numberOfVisibleRatePlans <=
                ratePlanMonitoringUpdate.data.length - 1
            }
            loader={
              <div key={0} className={classes.loadingContainer}>
                <CircularProgress size={32} />
              </div>
            }
            useWindow={true}>
            {ratePlanMonitoringUpdate?.data
              .slice(0, numberOfVisibleRatePlans)
              .map((dataRatePlane, indexRatePlane) => (
                <RatePlaneLayer
                  key={indexRatePlane}
                  dataRatePlane={dataRatePlane}
                  range={range}
                  weekendIndexes={weekendIndexes}
                  showChannelsLayer={showChannelsLayer}
                />
              ))}
          </InfiniteScroll>
        </div>
      </Collapse>
    </div>
  )
}
